import React, { useState } from "react";
import { Copy, Check, MessageCircle, Twitter, Share2 } from "lucide-react";
import { buildShareUrl } from "../hooks/useReferralCode";
import { TierProgress } from "./TierProgress";

const TIKTOK_SVG = (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.99a8.16 8.16 0 0 0 4.77 1.52V7.06a4.85 4.85 0 0 1-1.84-.37z"/>
    </svg>
);

const SHARE_COPY = "I just joined the Fits Me closed beta — fit-aware shopping across stores. Skip the line & grab a spot 👇";

/**
 * Brutalist share row used in waitlist success states. Shows:
 *   – queue-bump explainer ("Each invite bumps you up the line")
 *   – row of share buttons (Copy, X/Twitter, WhatsApp, TikTok, native Share API)
 *   – live referrals_count + position
 *
 * Props:
 *   referralCode (string)     — REQUIRED. The user's own code.
 *   referralsCount (number)   — optional. How many people they've invited.
 *   compact (bool)            — if true, hides the explainer copy and only shows the buttons.
 *   onCopy / onShare          — optional analytics hooks.
 */
export const ShareBeta = ({ referralCode, referralsCount = 0, tier, compact = false, onCopy, onShare }) => {
    const [copied, setCopied] = useState(false);
    const url = buildShareUrl(referralCode);
    if (!referralCode) return null;

    const fullText = `${SHARE_COPY} ${url}`;
    const enc = encodeURIComponent;

    const copy = async () => {
        // 1. Modern Clipboard API (Safari/Chromium with user gesture)
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
            if (onCopy) onCopy(referralCode);
            return;
        } catch (_e) { /* falls through to legacy path */ }
        // 2. Legacy execCommand fallback (older Safari, in-WebView contexts)
        try {
            const ta = document.createElement("textarea");
            ta.value = url;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
                if (onCopy) onCopy(referralCode);
                return;
            }
        } catch (_e) { /* falls through to prompt */ }
        // 3. Last resort: native prompt so the user can still grab the link
        window.prompt("Copy this link:", url);
    };

    const nativeShare = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: "Fits Me — Closed beta", text: SHARE_COPY, url });
                if (onShare) onShare("native");
            }
        } catch (_e) { /* user cancelled — silent */ }
    };

    const btnBase = "border-2 border-black bg-white p-2 shadow-brutal-sm btn-press inline-flex items-center justify-center";

    return (
        <div data-testid="share-beta" className="space-y-3">
            {tier && <TierProgress tier={tier} referralsCount={referralsCount} />}

            {!compact && (
                <div className="border-2 border-black bg-[#CCFF00] px-3 py-2 font-mono text-[11px] leading-relaxed">
                    <strong className="uppercase tracking-widest">Skip the line.</strong> Each friend that joins via your link bumps you up. You&apos;ve invited <span data-testid="share-beta-count" className="bg-black text-[#CCFF00] px-1.5 font-bold">{referralsCount}</span> so far.
                </div>
            )}

            {/* Copy-able link row */}
            <div className="border-2 border-black bg-white flex items-stretch overflow-hidden">
                <input
                    readOnly
                    aria-label="Your invite link"
                    data-testid="share-beta-link"
                    value={url}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 font-mono text-xs text-black/80 bg-white focus:outline-none focus:bg-[#CCFF00] truncate"
                />
                <button
                    onClick={copy}
                    data-testid="share-beta-copy"
                    aria-label="Copy link"
                    className="border-l-2 border-black bg-black text-[#CCFF00] px-3 font-mono text-[10px] uppercase tracking-widest btn-press inline-flex items-center gap-1"
                >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>

            {/* Channel buttons */}
            <div className="flex items-center gap-2 flex-wrap">
                <a
                    href={`https://twitter.com/intent/tweet?text=${enc(SHARE_COPY)}&url=${enc(url)}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => onShare && onShare("twitter")}
                    data-testid="share-beta-twitter"
                    aria-label="Share on X (Twitter)"
                    title="Share on X"
                    className={btnBase}
                >
                    <Twitter className="w-4 h-4" />
                </a>
                <a
                    href={`https://wa.me/?text=${enc(fullText)}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => onShare && onShare("whatsapp")}
                    data-testid="share-beta-whatsapp"
                    aria-label="Share on WhatsApp"
                    title="Share on WhatsApp"
                    className={btnBase}
                >
                    <MessageCircle className="w-4 h-4" />
                </a>
                <a
                    href={`https://www.tiktok.com/upload?caption=${enc(fullText)}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => onShare && onShare("tiktok")}
                    data-testid="share-beta-tiktok"
                    aria-label="Share on TikTok"
                    title="Share on TikTok"
                    className={btnBase}
                >
                    {TIKTOK_SVG}
                </a>
                {typeof navigator !== "undefined" && navigator.share && (
                    <button
                        onClick={nativeShare}
                        data-testid="share-beta-native"
                        aria-label="Share via system"
                        title="Share"
                        className={btnBase}
                    >
                        <Share2 className="w-4 h-4" />
                    </button>
                )}
                <span className="ml-1 font-mono text-[10px] uppercase tracking-widest text-black/50">
                    Code: <span className="bg-black text-[#CCFF00] px-1.5 py-0.5 font-bold tracking-[0.2em]" data-testid="share-beta-code">{referralCode}</span>
                </span>
            </div>
        </div>
    );
};
